/** Violation code metadata for WRH compliance UI and print report. */

export type WrhRulesThresholds = {
  id?: number;
  name?: string;
  isActive?: boolean;
  minRest24h: number;
  minRest7d: number;
  maxWork24h: number;
  maxRestSplits: number;
  minLongestRestSegment: number;
  maxGapBetweenRest: number;
};

export type ComplianceCodeMeta = {
  code: string;
  short: string;
  title: string;
  explain: (rules?: WrhRulesThresholds | null) => string;
  tone: 'rose' | 'amber' | 'orange';
};

export const COMPLIANCE_CODE_META: ComplianceCodeMeta[] = [
  {
    code: 'REST_24H',
    short: 'Rest 24h',
    title: 'Insufficient rest in 24 hours',
    explain: (r) =>
      `Total rest in any 24-hour period is below the minimum of ${Number(r?.minRest24h ?? 10)} hours (MLC / STCW).`,
    tone: 'rose',
  },
  {
    code: 'WORK_24H',
    short: 'Work 24h',
    title: 'Excessive work in 24 hours',
    explain: (r) =>
      `Total work in any 24-hour period exceeds the maximum of ${Number(r?.maxWork24h ?? 14)} hours.`,
    tone: 'rose',
  },
  {
    code: 'REST_7D',
    short: 'Rest 7d',
    title: 'Insufficient rest over 7 days',
    explain: (r) =>
      `Total rest in any 7-day period is below the minimum of ${Number(r?.minRest7d ?? 77)} hours.`,
    tone: 'orange',
  },
  {
    code: 'REST_SPLIT',
    short: 'Rest split',
    title: 'Rest periods incorrectly split',
    explain: (r) =>
      `Checked in every sliding 24h window: rest may be split into at most ${Number(r?.maxRestSplits ?? 2)} periods, and one must be at least ${Number(r?.minLongestRestSegment ?? 6)}h continuous. Rest before the morning watch (e.g. 20:00–08:00) counts as one block.`,
    tone: 'amber',
  },
  {
    code: 'REST_GAP',
    short: 'Rest gap',
    title: 'Gap between rest periods too long',
    explain: (r) =>
      `The gap between consecutive rest periods (or work spanning the day edges) exceeds ${Number(r?.maxGapBetweenRest ?? 14)} hours.`,
    tone: 'amber',
  },
];

export const COMPLIANCE_META_BY_CODE = Object.fromEntries(
  COMPLIANCE_CODE_META.map((m) => [m.code, m]),
) as Record<string, ComplianceCodeMeta>;

export function fmtComplianceHours(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}
